import type { Router } from 'vue-router'

/**
 * After a deploy the old JS chunks are gone (rsync --delete), so a tab that was open
 * before it fails to load the next lazy route ("Failed to fetch dynamically imported
 * module", or Vite's vite:preloadError for a chunk's dependencies). Reload once into the
 * new build: the target route when we know it, the current page otherwise. A short
 * guard in sessionStorage stops a reload loop if the chunk really is missing.
 */
const GUARD_KEY = 'nerdcrm:stale-chunk-reload-at'
const GUARD_MS = 10_000
const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

function reloadOnce(url?: string): boolean {
  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY) || 0)
    if (Date.now() - last < GUARD_MS) return false
    sessionStorage.setItem(GUARD_KEY, String(Date.now()))
  } catch {
    // storage blocked: still reload, a loop is unlikely without the guard ever firing twice
  }
  if (url) window.location.assign(url)
  else window.location.reload()
  return true
}

export function installStaleChunkReload(router: Router): void {
  // Vite fires this before the failed import rejects. Let the rejection reach the router
  // first (it knows the destination and reloads straight into it); only reload the current
  // page if nothing else handled it, e.g. a dynamic import outside routing.
  window.addEventListener('vite:preloadError', () => {
    setTimeout(() => reloadOnce(), 300)
  })
  router.onError((err, to) => {
    if (CHUNK_ERROR.test(String((err as Error)?.message ?? err))) reloadOnce(to?.fullPath)
  })
}
